'use client';

import React, { useState, useEffect } from 'react';
import { Tabs } from '@/components/ui/Tabs';
import Icon from '@/components/ui/icons/Icon';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// ─── Tipos internos ──────────────────────────────────────────────────────────

interface EndpointRow {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  path: string;
  description: string;
}

interface EndpointGroup {
  module: string;
  tag: string;
  endpoints: EndpointRow[];
}

// ─── Datos estáticos ──────────────────────────────────────────────────────────

const ENDPOINT_GROUPS: EndpointGroup[] = [
  {
    module: 'Auth',
    tag: 'auth',
    endpoints: [
      { method: 'POST', path: '/api/v1/auth/login', description: 'Autenticación. Verifica credenciales y retorna JWT en cookie HttpOnly (24 h). Rate limit: 5/min.' },
      { method: 'POST', path: '/api/v1/auth/logout', description: 'Cierra sesión eliminando la cookie auth_token.' },
    ],
  },
  {
    module: 'Usuarios',
    tag: 'users',
    endpoints: [
      { method: 'GET',    path: '/api/v1/users/',                       description: 'Lista usuarios con paginación (skip/limit, máx. 1000).' },
      { method: 'GET',    path: '/api/v1/users/{user_id}',              description: 'Detalle de usuario por owner_id.' },
      { method: 'GET',    path: '/api/v1/users/acl/{acl_name}',        description: 'Usuarios filtrados por rol (ACL).' },
      { method: 'GET',    path: '/api/v1/users/puesto/{puesto}',        description: 'Usuarios filtrados por puesto.' },
      { method: 'GET',    path: '/api/v1/users/name/{name}',            description: 'Usuario por nombre de usuario.' },
      { method: 'POST',   path: '/api/v1/users/',                       description: 'Crea usuario. Requiere rol sysAdmin o gerenteComercial.' },
      { method: 'PUT',    path: '/api/v1/users/{user_id}',              description: 'Actualiza datos del usuario.' },
      { method: 'DELETE', path: '/api/v1/users/{user_id}',              description: 'Elimina usuario (solo sysAdmin).' },
      { method: 'POST',   path: '/api/v1/users/{user_id}/change-password', description: 'Cambia contraseña. Hash: SHA512(pass+correo).' },
      { method: 'POST',   path: '/api/v1/users/{user_id}/upload-photo', description: 'Sube foto de perfil del usuario.' },
      { method: 'POST',   path: '/api/v1/users/admin-reset-password',   description: 'Reset de contraseña por admin (solo sysAdmin).' },
    ],
  },
  {
    module: 'Solicitudes',
    tag: 'solicitudes',
    endpoints: [
      { method: 'GET',  path: '/api/v1/solicitudes/',                         description: 'Todas las solicitudes con paginación (máx. 5000).' },
      { method: 'GET',  path: '/api/v1/solicitudes/summary',                  description: 'Versión resumida (menos campos) para tablas ligeras.' },
      { method: 'GET',  path: '/api/v1/solicitudes/in-process',               description: 'En proceso (excluye FONDEADO y RECHAZADO).' },
      { method: 'GET',  path: '/api/v1/solicitudes/contactados',              description: 'Estatus CONTACTO — módulo Contactados.' },
      { method: 'GET',  path: '/api/v1/solicitudes/entregados-a-riesgos',     description: 'Estatus ENTREGA A RIESGOS — módulo Entregados.' },
      { method: 'GET',  path: '/api/v1/solicitudes/revision-documental',      description: 'Sin VoBo, revisión documental pendiente.' },
      { method: 'GET',  path: '/api/v1/solicitudes/pre-analisis',             description: 'Revisión doc. completada, análisis pendiente.' },
      { method: 'GET',  path: '/api/v1/solicitudes/analisis-creditos',        description: 'Módulo Análisis de Créditos.' },
      { method: 'GET',  path: '/api/v1/solicitudes/autorizacion-riesgos',     description: 'Con VoBo aprobado, pendientes de autorizar.' },
      { method: 'GET',  path: '/api/v1/solicitudes/autorizacion-dg',          description: 'EN AUTORIZACION DG con autorizacionDG=Requerida.' },
      { method: 'GET',  path: '/api/v1/solicitudes/imprimir-contrato',        description: 'Estatus AUTORIZADO — módulo Imprimir Contrato.' },
      { method: 'GET',  path: '/api/v1/solicitudes/firmar-contrato',          description: 'Estatus CONTRATO IMPRESO — módulo Firmar Contrato.' },
      { method: 'GET',  path: '/api/v1/solicitudes/por-fondear',              description: 'Estatus FIRMAR CONTRATO, contrato aún no validado.' },
      { method: 'GET',  path: '/api/v1/solicitudes/transferencias',           description: 'FIRMAR CONTRATO con contrato Ok, listas para transferir.' },
      { method: 'GET',  path: '/api/v1/solicitudes/fondeados',                description: 'FONDEADO del mes actual.' },
      { method: 'GET',  path: '/api/v1/solicitudes/rechazados',               description: 'Rechazadas (mes actual o históricas con ?solo_mes_actual=false).' },
      { method: 'GET',  path: '/api/v1/solicitudes/seguimiento-diario',       description: 'Reporte de seguimiento diario con alertas visuales.' },
      { method: 'GET',  path: '/api/v1/solicitudes/{solicitud_id}',           description: 'Detalle de una solicitud por ID numérico.' },
      { method: 'POST', path: '/api/v1/solicitudes/',                         description: 'Crea solicitud en estatus CONTACTO.' },
      { method: 'PUT',  path: '/api/v1/solicitudes/{solicitud_id}',           description: 'Actualización campo por campo (field+value) o masiva.' },
      { method: 'POST', path: '/api/v1/solicitudes/{id}/realizar-analisis',   description: 'Ejecuta pre-análisis y genera 3 propuestas.' },
      { method: 'POST', path: '/api/v1/solicitudes/{id}/realizar-analisis-creditos', description: 'Análisis directo sin propuestas intermedias.' },
      { method: 'POST', path: '/api/v1/solicitudes/{id}/aceptar-propuesta',   description: 'Acepta propuesta (Solicitud/BuenBuro/Recomendacion).' },
      { method: 'POST', path: '/api/v1/solicitudes/{id}/simular-siac',        description: 'Simulación SIAC para calcular valores del contrato.' },
    ],
  },
  {
    module: 'Empresas',
    tag: 'empresas',
    endpoints: [
      { method: 'GET', path: '/api/v1/empresas/',                          description: 'Catálogo completo de empresas (paginado).' },
      { method: 'GET', path: '/api/v1/empresas/restringidas',              description: 'Empresas con acceso restringido por convenio.' },
      { method: 'GET', path: '/api/v1/empresas/asignaciones',              description: 'Vista de asignaciones: asesores con sus empresas.' },
      { method: 'GET', path: '/api/v1/empresas/por-asesor/{owner_id}',    description: 'Empresas activas asignadas a un asesor específico.' },
      { method: 'GET', path: '/api/v1/empresas/{empresa_id}',             description: 'Detalle de empresa por ID.' },
      { method: 'POST', path: '/api/v1/empresas/',                         description: 'Crea empresa en el catálogo.' },
      { method: 'PUT',  path: '/api/v1/empresas/{empresa_id}',            description: 'Actualiza empresa existente.' },
      { method: 'GET',  path: '/api/v1/empresas/{empresa_id}/asignaciones', description: 'ownerIDs de asesores asignados a la empresa.' },
      { method: 'PUT',  path: '/api/v1/empresas/{empresa_id}/asignaciones', description: 'Asigna/desasigna un asesor. Actualiza empresa_users.' },
    ],
  },
  {
    module: 'ACL',
    tag: 'acl',
    endpoints: [
      { method: 'GET',    path: '/api/v1/acl/',                       description: 'Lista todos los ACLs del sistema.' },
      { method: 'GET',    path: '/api/v1/acl/by-name/{acl_name}',    description: 'ACL por nombre. Cualquier usuario autenticado puede consultar.' },
      { method: 'GET',    path: '/api/v1/acl/validate',              description: 'Valida que todos los roles requeridos estén definidos.' },
      { method: 'GET',    path: '/api/v1/acl/required-roles',        description: 'Lista de roles requeridos por el sistema.' },
      { method: 'POST',   path: '/api/v1/acl/',                      description: 'Crea nuevo ACL (solo sysAdmin).' },
      { method: 'PUT',    path: '/api/v1/acl/{acl_name}',           description: 'Actualiza ACL: scope y forbidden_fields (solo sysAdmin).' },
      { method: 'DELETE', path: '/api/v1/acl/{acl_name}',           description: 'Elimina ACL. No permite eliminar roles críticos (sysAdmin, all).' },
    ],
  },
  {
    module: 'Reportes',
    tag: 'reportes',
    endpoints: [
      { method: 'GET',  path: '/api/v1/reportes/pipeline',    description: 'Datos del Pipeline: solicitudes agrupadas con intervalos.' },
      { method: 'GET',  path: '/api/v1/reportes/scorecard',   description: 'ScoreCard: métricas de desempeño por asesor.' },
      { method: 'GET',  path: '/api/v1/reportes/colocacion',  description: 'Colocación: gráfica, tabular, excepciones o expedientes faltantes.' },
      { method: 'GET',  path: '/api/v1/reportes/cumplimiento', description: 'Cumplimiento: excepciones a políticas o expedientes faltantes.' },
      { method: 'POST', path: '/api/v1/reportes/texto',        description: 'Reporte CSV personalizado por catálogo (Solicitudes/Empresas/Agendas).' },
    ],
  },
  {
    module: 'Simulador',
    tag: 'simulador',
    endpoints: [
      { method: 'POST', path: '/api/v1/simulador/', description: 'Simula crédito: pagos, intereses, tabla de amortización.' },
      { method: 'GET',  path: '/api/v1/simulador/parametros', description: 'Parámetros disponibles del simulador para poblar el frontend.' },
    ],
  },
  {
    module: 'Cosechas',
    tag: 'cosechas',
    endpoints: [
      { method: 'POST', path: '/api/v1/cosechas/upload',      description: 'Sube CSV con datos de cartera vencida y los procesa.' },
      { method: 'GET',  path: '/api/v1/cosechas/periodos',    description: 'Períodos, meses de otorgamiento, buckets y productos disponibles.' },
      { method: 'GET',  path: '/api/v1/cosechas/tabular',     description: 'Reporte tabular de cosechas (Periodo/Otorgamiento o viceversa).' },
      { method: 'GET',  path: '/api/v1/cosechas/export-csv',  description: 'Exporta reporte tabular a CSV.' },
      { method: 'GET',  path: '/api/v1/cosechas/grafica',     description: 'Datos para gráfica de cosechas compatible con Chart.js.' },
    ],
  },
  {
    module: 'Backups',
    tag: 'backups',
    endpoints: [
      { method: 'GET',    path: '/api/v1/backups/',                           description: 'Lista backups disponibles. Solo sysAdmin.' },
      { method: 'POST',   path: '/api/v1/backups/generate',                   description: 'Genera backup comprimido (.bku) de la BD. Solo sysAdmin.' },
      { method: 'POST',   path: '/api/v1/backups/generate-sql',               description: 'Genera .sql directo desde la BD. Solo sysAdmin.' },
      { method: 'GET',    path: '/api/v1/backups/{filename}',                 description: 'Descarga archivo de backup (.bku o .sql). Solo sysAdmin.' },
      { method: 'POST',   path: '/api/v1/backups/{bku_filename}/generate-sql', description: 'Genera .sql desde un .bku existente. Solo sysAdmin.' },
      { method: 'DELETE', path: '/api/v1/backups/{filename}',                 description: 'Elimina archivo de backup. Solo sysAdmin.' },
    ],
  },
  {
    module: 'Logs',
    tag: 'logs',
    endpoints: [
      { method: 'GET', path: '/api/v1/logs/', description: 'Logs de auditoría (UpdateLog) con filtro por fecha y campo. Default: mes anterior.' },
    ],
  },
  {
    module: 'Agendas',
    tag: 'agendas',
    endpoints: [
      { method: 'GET',  path: '/api/v1/agendas/',          description: 'Agendas en rango de fechas. Sin rango retorna todas.' },
      { method: 'POST', path: '/api/v1/agendas/',          description: 'Crea nuevo evento en la agenda.' },
      { method: 'GET',  path: '/api/v1/agendas/calendario', description: 'Vista de calendario: asesores con días y horas.' },
      { method: 'POST', path: '/api/v1/agendas/guardar',   description: 'Crea o actualiza agenda (por ownerID + fecha + hora).' },
    ],
  },
  {
    module: 'Sesiones',
    tag: 'sesiones',
    endpoints: [
      { method: 'GET', path: '/api/v1/sesiones/', description: 'Sesiones activas de usuarios. Registra actividad del usuario actual.' },
    ],
  },
  {
    module: 'ROIPs',
    tag: 'roips',
    endpoints: [
      { method: 'GET',    path: '/api/v1/roips/',                                 description: 'Lista ROIPs filtrados por scope del usuario.' },
      { method: 'GET',    path: '/api/v1/roips/{roip_id}',                        description: 'ROIP por ID.' },
      { method: 'POST',   path: '/api/v1/roips/',                                 description: 'Crea ROIP. Establece reportadoPor, fechaReporte y reporteEstatus.' },
      { method: 'PUT',    path: '/api/v1/roips/{roip_id}',                        description: 'Actualiza ROIP (no modifica el campo roi).' },
      { method: 'PUT',    path: '/api/v1/roips/{roip_id}/toggle-fecha-dictaminacion', description: 'Activa/desactiva fechaDictaminacion con fecha actual.' },
      { method: 'PUT',    path: '/api/v1/roips/{roip_id}/toggle-fecha-reporte-siti', description: 'Activa/desactiva fechaReporteSITI con fecha actual.' },
      { method: 'DELETE', path: '/api/v1/roips/{roip_id}',                        description: 'Elimina ROIP.' },
    ],
  },
  {
    module: 'Visitas',
    tag: 'visitas',
    endpoints: [
      { method: 'GET',    path: '/api/v1/visitas/',           description: 'Visitas en rango de fechas (ISO o YYYY-MM-DD HH:MM:SS).' },
      { method: 'POST',   path: '/api/v1/visitas/',           description: 'Registra visita con coordenadas de geolocalización.' },
      { method: 'GET',    path: '/api/v1/visitas/{id}',       description: 'Detalle de visita por ID.' },
      { method: 'PUT',    path: '/api/v1/visitas/{id}',       description: 'Actualiza visita existente.' },
      { method: 'DELETE', path: '/api/v1/visitas/{id}',       description: 'Elimina visita.' },
    ],
  },
  {
    module: 'CLABEs',
    tag: 'clabes',
    endpoints: [
      { method: 'GET',  path: '/api/v1/clabes/solicitudes', description: 'Solicitudes para módulo CLABEs: ENTREGA A RIESGOS, cliente nuevo, con CLABE.' },
      { method: 'GET',  path: '/api/v1/clabes/listado',     description: 'Listado de CLABEs pendientes de validación.' },
      { method: 'POST', path: '/api/v1/clabes/validar',     description: 'Actualiza fechaValidacionClabe (true=fecha actual, false=limpia).' },
    ],
  },
  {
    module: 'Cobranza',
    tag: 'cobranza',
    endpoints: [
      { method: 'GET',  path: '/api/v1/cobranza/solicitudes', description: 'Solicitudes FONDEADO para módulo Cobranza.' },
      { method: 'POST', path: '/api/v1/cobranza/validar',     description: 'Actualiza fechaReferenciaCobranza.' },
      { method: 'GET',  path: '/api/v1/cobranza/reporte',     description: 'CSV de solicitudes con referencia de cobranza.' },
    ],
  },
  {
    module: 'Pagadoras',
    tag: 'pagadoras',
    endpoints: [
      { method: 'GET', path: '/api/v1/pagadoras/', description: 'Configuración de pagadoras: frecuencias y retenciones.' },
      { method: 'PUT', path: '/api/v1/pagadoras/', description: 'Actualiza configuración de pagadoras.' },
    ],
  },
  {
    module: 'Expedientes',
    tag: 'expedientes',
    endpoints: [
      { method: 'GET', path: '/api/v1/expedientes/solicitudes', description: 'Solicitudes para Control de Expedientes (26 campos optimizados).' },
    ],
  },
  {
    module: 'Parametricos',
    tag: 'parametricos',
    endpoints: [
      { method: 'GET',  path: '/api/v1/parametricos/',              description: 'Lista todos los parámetros editables del sistema.' },
      { method: 'GET',  path: '/api/v1/parametricos/{clave}',       description: 'Obtiene un parámetro por su clave.' },
      { method: 'POST', path: '/api/v1/parametricos/',              description: 'Crea un parámetro nuevo. Solo sysAdmin.' },
      { method: 'PUT',  path: '/api/v1/parametricos/{clave}',       description: 'Actualiza el valor de un parámetro. Solo sysAdmin.' },
      { method: 'POST', path: '/api/v1/parametricos/seed',          description: 'Inicializa parámetros desde core/parametricos.py.' },
      { method: 'GET',  path: '/api/v1/parametricos/buro/{tipo}',   description: 'Tasas, comisiones y plazos por tipo de buró (BUENO/REGULAR).' },
    ],
  },
  {
    module: 'Email Config',
    tag: 'email_config',
    endpoints: [
      { method: 'GET',    path: '/api/v1/email-config/',         description: 'Obtiene la configuración SMTP actual.' },
      { method: 'POST',   path: '/api/v1/email-config/',         description: 'Crea configuración SMTP. Solo sysAdmin.' },
      { method: 'PUT',    path: '/api/v1/email-config/{id}',     description: 'Actualiza configuración SMTP. Solo sysAdmin.' },
      { method: 'DELETE', path: '/api/v1/email-config/{id}',     description: 'Elimina configuración SMTP. Solo sysAdmin.' },
    ],
  },
  {
    module: 'Health',
    tag: 'health',
    endpoints: [
      { method: 'GET', path: '/api/v1/health', description: 'Estado del backend. Sin DB. Usado para health checks en Cloud Run / ALB / Container Apps.' },
    ],
  },
];

const METHOD_COLORS: Record<string, string> = {
  GET:    'bg-blue-100 text-blue-700',
  POST:   'bg-green-100 text-green-700',
  PUT:    'bg-yellow-100 text-yellow-700',
  DELETE: 'bg-red-100 text-red-700',
};

// ─── Sub-componentes de tabs ──────────────────────────────────────────────────

function SectionTitle({ children }: { children: React.ReactNode }) {
  return <h2 className="text-lg font-bold text-gray-800 mb-3 mt-6 first:mt-0">{children}</h2>;
}

function InfoTable({ headers, rows }: { headers: string[]; rows: (string | React.ReactNode)[][] }) {
  return (
    <div className="overflow-x-auto mb-6">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="bg-gray-50">
            {headers.map((h) => (
              <th key={h} className="text-left px-3 py-2 font-semibold text-gray-700 border border-gray-200">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
              {row.map((cell, j) => (
                <td key={j} className="px-3 py-2 border border-gray-200 text-gray-700">{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CodeBlock({ children }: { children: string }) {
  return (
    <pre className="bg-gray-900 text-green-300 rounded-lg p-4 text-xs overflow-x-auto mb-4 leading-relaxed">
      {children}
    </pre>
  );
}

// ─── Tab 1: Visión General ────────────────────────────────────────────────────

function TabVisionGeneral() {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          { icon: 'chart' as const, label: '110', sub: 'Endpoints API' },
          { icon: 'users' as const, label: '10', sub: 'Roles ACL' },
          { icon: 'file' as const, label: '43', sub: 'Páginas de menú' },
          { icon: 'lock' as const, label: '5', sub: 'Columnas cifradas (Fernet)' },
        ].map((s) => (
          <div key={s.label} className="bg-teal-50 border border-teal-100 rounded-xl p-4 flex items-center gap-4">
            <div className="p-3 bg-white rounded-lg shadow-sm">
              <Icon name={s.icon} size={22} color="#0196A1" />
            </div>
            <div>
              <p className="text-2xl font-bold text-teal-700">{s.label}</p>
              <p className="text-sm text-teal-600">{s.sub}</p>
            </div>
          </div>
        ))}
      </div>

      <SectionTitle>Descripción</SectionTitle>
      <p className="text-gray-600 text-sm leading-relaxed">
        <strong>PipeEnLinea / MiPymex CAABSA</strong> es una plataforma integral para la gestión del ciclo de vida
        completo de solicitudes de crédito de nómina: desde la captación inicial (Contacto) hasta el fondeo del
        crédito, con control documental, análisis crediticio, autorización por áreas y seguimiento de cobranza.
      </p>

      <SectionTitle>Stack Tecnológico</SectionTitle>
      <InfoTable
        headers={['Componente', 'Tecnología', 'Puerto', 'Directorio']}
        rows={[
          ['Backend API', 'FastAPI (Python 3.11) + SQLModel + Pydantic v2', '8000', 'mipymex-caabsa-backend/'],
          ['Frontend UI', 'Next.js 16.1.6 (React 18 + TypeScript 5.3 strict)', '3000', 'mipymex-caabsa-frontend/'],
          ['Base de Datos', 'PostgreSQL 16 (Cloud SQL / RDS / Azure DB)', '5432 (prod) · 5434 (dev)', '—'],
          ['Encriptación BD', 'Fernet (AES-128-CBC + HMAC-SHA256) a nivel de columna', '—', 'backend/core/encryption.py'],
          ['Auth', 'JWT HS256 (24 h) + bcrypt(SHA512(pass+correo)[:72])', '—', 'backend/services/auth_service.py'],
          ['Contenedores', 'Docker multi-stage · imagen única para AWS/GCP/Azure', '—', 'Dockerfile / Dockerfile.dev'],
          ['Infraestructura', 'Cloud Run · ECS Fargate · Azure Container Apps', '—', 'cloudbuild.yaml'],
        ]}
      />

      <SectionTitle>Arquitectura Backend</SectionTitle>
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm font-mono text-gray-700">
        <p>Routes (HTTP) → Schemas (Pydantic) → Services (Lógica de negocio)</p>
        <p className="ml-12 text-gray-400">↓</p>
        <p className="ml-8">Repositories (Data Access) → Models (SQLModel ORM)</p>
      </div>
      <InfoTable
        headers={['Capa', 'Directorio', 'Responsabilidad']}
        rows={[
          ['Routes', 'routes/', '21 routers con 110 endpoints HTTP bajo /api/v1'],
          ['Schemas', 'schemas/', 'Validación Pydantic de entrada/salida y aliases camelCase'],
          ['Services', 'services/', '22 servicios. SolicitudService usa 7 Mixins (crud, queries, análisis, cálculos, propuestas, state_machine, helpers)'],
          ['Repositories', 'repositories/', '14 repositorios con CRUD genérico sobre SQLModel'],
          ['Models', 'models/', '16 modelos / tablas. Modelo central Solicitud con 190+ campos'],
          ['Core', 'core/', 'database.py (pool, auto-migración) · encryption.py (Fernet) · parametricos.py · auth.py (JWT)'],
        ]}
      />

      <SectionTitle>Arquitectura Frontend</SectionTitle>
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm font-mono text-gray-700">
        <p>App Router → AuthGuard → Dashboard Layout (Header + Sidebar + Content) → Feature Pages</p>
      </div>
      <InfoTable
        headers={['Capa', 'Ruta', 'Responsabilidad']}
        rows={[
          ['App Router', 'src/app/', 'Route groups (auth) y [...slug] catch-all para el dashboard'],
          ['Config', 'src/config/dashboardPages.ts', 'SSOT de las 43 páginas del menú (nombre, ícono, componente, URL, ACL)'],
          ['Features', 'src/components/features/', '~70 componentes organizados por dominio (patrón: Management→Table→Detail→Form)'],
          ['API Layer', 'src/lib/api/', 'apiClient.ts + 20 servicios tipados (fetch wrapper con credentials:"include")'],
          ['Hooks', 'src/hooks/', 'useAuth (ACL + forbidden_fields), useDashboard, useDataLoader, usePlazosDisponibles, useSharedData'],
          ['Types', 'src/types/', '15 archivos con interfaces TypeScript para todos los modelos del backend'],
          ['Middleware', 'middleware.ts', 'Edge Runtime: valida expiración del JWT en cookie auth_token y redirige a /login'],
          ['Utils', 'src/utils/', 'fieldVisibility.ts (18 módulos) · auth.ts (clearSession, checkToken)'],
        ]}
      />

      <SectionTitle>Tablas Principales de Base de Datos</SectionTitle>
      <InfoTable
        headers={['Tabla', 'Descripción', 'Cifrado']}
        rows={[
          ['users', 'Usuarios del sistema: credenciales, rol ACL, scope, api_key', 'api_key (Fernet)'],
          ['acl', 'Definición de roles: scope y forbidden_fields por rol', '—'],
          ['solicitudes', 'Modelo central (190+ campos): ciclo de vida completo de créditos', 'cliente_nombre, cliente_apellido_paterno, cliente_apellido_materno, cliente_clabe (Fernet)'],
          ['update_log', 'Auditoría: cada cambio CRUD queda registrado (~91K registros)', '—'],
          ['empresas', 'Catálogo de empresas pagadoras', '—'],
          ['empresa_user', 'Relación M2M entre empresas y asesores (asignaciones)', '—'],
          ['agendas', 'Citas y eventos del calendario de asesores', '—'],
          ['roips', 'Registro de Operaciones Internas Preocupantes', '—'],
          ['visitas', 'Visitas a clientes con coordenadas GPS', '—'],
          ['sesiones', 'Sesiones activas de usuarios', '—'],
          ['cosechas', 'Datos de cartera vencida por cosecha (vintage)', '—'],
          ['cosechas_vencimientos', 'Vencimientos por bucket (30, 60, 90... días)', '—'],
          ['pagadoras_config', 'Configuración de frecuencias y retenciones por pagadora', '—'],
          ['parametricos', 'Parámetros de negocio editables desde UI (complementa parametricos.py)', '—'],
          ['email_config', 'Configuración SMTP para notificaciones', '—'],
        ]}
      />
    </div>
  );
}

// ─── Tab 2: Módulos del Sistema ───────────────────────────────────────────────

function TabModulos() {
  const modulosFlujo = [
    { name: 'Inicio', url: '/home', desc: 'Pantalla de bienvenida con resumen de la plataforma.' },
    { name: 'Solicitudes', url: '/solicitudes', desc: 'Vista principal de todas las solicitudes activas en el sistema.' },
    { name: 'Contactados', url: '/contactados', desc: 'Solicitudes en etapa inicial CONTACTO. Aquí el asesor captura al cliente.' },
    { name: 'Entregados a Riesgos', url: '/entregadosariesgos', desc: 'Solicitudes entregadas al área de análisis y riesgos.' },
    { name: 'Mesa de Análisis', url: '/mesadeanalisis', desc: 'Vista consolidada para el equipo de análisis crediticio.' },
    { name: 'Revisión Documental', url: '/revisionDocumental', desc: 'Verificación de documentos del cliente (INE, comprobantes, etc.).' },
    { name: 'Pre Análisis', url: '/pre-analisis', desc: 'Análisis crediticio preliminar: genera 3 propuestas de crédito.' },
    { name: 'Análisis de Créditos', url: '/analisis-creditos', desc: 'Análisis crediticio completo con actualización directa de valores.' },
    { name: "Solicitar VoBo's", url: '/solicitarvobos', desc: 'Gestión de visto bueno de las áreas involucradas en la autorización.' },
    { name: 'Autorización Riesgos', url: '/autorizacionriesgos', desc: 'Aprobación por el Gerente de Riesgos.' },
    { name: 'Autorización DG', url: '/autorizaciondg', desc: 'Aprobación de Dirección General (solo cuando se requiere VoBo DG).' },
    { name: 'Imprimir Contrato', url: '/imprimircontrato', desc: 'Generación e impresión del contrato para créditos AUTORIZADOS.' },
    { name: 'Firmar Contrato', url: '/firmarcontrato', desc: 'Firma del contrato por el cliente (estatus CONTRATO IMPRESO).' },
    { name: 'Por Fondear', url: '/porfondear', desc: 'Solicitudes listas para dispersión del crédito.' },
    { name: 'Transferencias', url: '/transferencias', desc: 'Gestión de transferencias bancarias a los clientes.' },
    { name: 'Fondeados', url: '/fondeados', desc: 'Créditos ya dispersados del mes actual.' },
    { name: 'Rechazados', url: '/rechazados', desc: 'Solicitudes rechazadas en cualquier etapa del flujo.' },
    { name: 'CLABEs', url: '/clabes', desc: 'Validación de cuentas bancarias CLABE para clientes nuevos.' },
    { name: 'Cobranza', url: '/cobranza', desc: 'Seguimiento de pagos y gestión de cartera vencida.' },
    { name: 'Control Expedientes', url: '/expedientes', desc: 'Gestión de expedientes físicos: recepción y seguimiento.' },
    { name: 'Histórico', url: '/historico', desc: 'Historial completo de solicitudes (todos los estatus).' },
  ];
  const modulosReportes = [
    { name: 'Simulador', url: '/simulador', desc: 'Simulador de créditos: calcula pagos, tasas y capacidad de pago.' },
    { name: 'Seguimiento Diario', url: '/seguimiento-diario', desc: 'Reporte diario de solicitudes en proceso con alertas de tiempos.' },
    { name: 'Pipeline', url: '/pipeline', desc: 'Embudo de ventas: solicitudes agrupadas por etapa y período.' },
    { name: 'ScoreCard', url: '/scorecard', desc: 'Métricas de desempeño por asesor (conversión, tiempos, montos).' },
    { name: 'Colocación', url: '/colocacion', desc: 'Análisis de colocación: gráfica, tabular, excepciones y expedientes.' },
    { name: 'Cumplimiento', url: '/cumplimiento', desc: 'Reporte de cumplimiento regulatorio y expedientes faltantes.' },
    { name: 'Cosechas', url: '/cosechas', desc: 'Análisis de cartera vencida por cosecha (vintage) con gráficas.' },
    { name: 'OIP', url: '/oip', desc: 'Operaciones Internas Preocupantes (ROIPs).' },
    { name: 'Reporte Ventas', url: '/reporte-ventas', desc: 'Reporte ejecutivo de ventas por período.' },
    { name: 'Agendas', url: '/agendas', desc: 'Calendario de citas y eventos por asesor.' },
    { name: 'Logs', url: '/logs', desc: 'Auditoría: historial de cambios en solicitudes (UpdateLog).' },
    { name: 'Visitas', url: '/visitas', desc: 'Registro de visitas a clientes con geolocalización.' },
    { name: 'BackUps', url: '/backups', desc: 'Gestión de respaldos de base de datos. Solo sysAdmin.' },
    { name: 'Sesiones Activas', url: '/sesiones', desc: 'Usuarios con sesión activa en el sistema.' },
    { name: 'Reporte de Texto', url: '/reporte-texto', desc: 'Generador de reportes CSV personalizados por catálogo.' },
  ];
  const modulosAdmin = [
    { name: 'Usuarios', url: '/usuarios', desc: 'Gestión de usuarios: crear, editar, desactivar, cambiar contraseña.' },
    { name: 'ACL', url: '/acl', desc: 'Control de acceso por rol: scopes y campos prohibidos. Solo sysAdmin.' },
    { name: 'Empresas', url: '/empresas', desc: 'Catálogo de empresas pagadoras: altas, bajas y asignaciones.' },
    { name: 'Pagadoras', url: '/pagadoras', desc: 'Configuración de frecuencias de pago y retenciones por empresa.' },
    { name: 'Asignaciones', url: '/asignaciones', desc: 'Asignación de asesores a empresas por región.' },
    { name: 'Perfil', url: '/perfil', desc: 'Perfil del usuario: datos personales y cambio de contraseña.' },
    { name: 'Ayuda', url: '/ayuda', desc: 'Documentación completa de la plataforma. Solo sysAdmin.' },
  ];

  return (
    <div className="space-y-2">
      <SectionTitle>Flujo de Solicitudes (21 módulos)</SectionTitle>
      <InfoTable
        headers={['Módulo', 'URL', 'Descripción']}
        rows={modulosFlujo.map((m) => [m.name, <code key={m.url} className="text-xs bg-gray-100 px-1 rounded">{m.url}</code>, m.desc])}
      />
      <SectionTitle>Reportes y Utilidades (15 módulos)</SectionTitle>
      <InfoTable
        headers={['Módulo', 'URL', 'Descripción']}
        rows={modulosReportes.map((m) => [m.name, <code key={m.url} className="text-xs bg-gray-100 px-1 rounded">{m.url}</code>, m.desc])}
      />
      <SectionTitle>Administración (7 módulos)</SectionTitle>
      <InfoTable
        headers={['Módulo', 'URL', 'Descripción']}
        rows={modulosAdmin.map((m) => [m.name, <code key={m.url} className="text-xs bg-gray-100 px-1 rounded">{m.url}</code>, m.desc])}
      />
    </div>
  );
}

// ─── Tab 3: Flujo de Solicitudes ──────────────────────────────────────────────

function TabFlujoSolicitudes() {
  const estados = [
    {
      estado: 'CONTACTO',
      color: 'bg-blue-100 border-blue-300 text-blue-800',
      modulo: 'Contactados',
      quien: 'Asesor',
      desc: 'El asesor captura los datos iniciales del cliente. Campos clave: nombre, empresa, sueldo, monto solicitado, tipo de crédito.',
    },
    {
      estado: 'ENTREGA A RIESGOS',
      color: 'bg-orange-100 border-orange-300 text-orange-800',
      modulo: 'Entregados a Riesgos',
      quien: 'Asesor → Riesgos',
      desc: 'El asesor entrega el expediente al área de Riesgos. Se inicia la revisión documental y el análisis crediticio.',
    },
    {
      estado: 'REVISIÓN DOCUMENTAL',
      color: 'bg-yellow-100 border-yellow-300 text-yellow-800',
      modulo: 'Revisión Documental',
      quien: 'Analista de Riesgos',
      desc: 'Se verifican documentos: INE, comprobante de domicilio, recibos de nómina, etc. Se activa el switch fechaRevisionDocumentalConcluida.',
    },
    {
      estado: 'PRE ANÁLISIS',
      color: 'bg-yellow-100 border-yellow-300 text-yellow-800',
      modulo: 'Pre Análisis',
      quien: 'Analista de Riesgos',
      desc: 'Análisis preliminar que genera 3 propuestas: propuestaSolicitud, propuestaBuenBuro y propuestaRecomendacion. Evalúa buró crediticio y capacidad de pago.',
    },
    {
      estado: 'ANÁLISIS DE CRÉDITOS',
      color: 'bg-yellow-100 border-yellow-300 text-yellow-800',
      modulo: 'Análisis de Créditos',
      quien: 'Analista de Riesgos',
      desc: 'Análisis crediticio completo. Actualiza valores directamente en la solicitud. Se activa fechaAnalisisConcluido.',
    },
    {
      estado: "SOLICITAR VoBo's",
      color: 'bg-purple-100 border-purple-300 text-purple-800',
      modulo: "Solicitar VoBo's",
      quien: 'Analista de Riesgos',
      desc: 'Se solicitan visto buenos de las áreas involucradas. Puede incluir VoBo de DG (Dirección General) si es requerido.',
    },
    {
      estado: 'AUTORIZACIÓN RIESGOS',
      color: 'bg-purple-100 border-purple-300 text-purple-800',
      modulo: 'Autorización Riesgos',
      quien: 'Gerente de Riesgos',
      desc: 'El Gerente de Riesgos aprueba el crédito. Si se requiere VoBo DG, la solicitud pasa a EN AUTORIZACIÓN DG.',
    },
    {
      estado: 'AUTORIZACIÓN DG (opcional)',
      color: 'bg-pink-100 border-pink-300 text-pink-800',
      modulo: 'Autorización DG',
      quien: 'Director General',
      desc: 'Solo cuando autorizacionDG=Requerida. El DG aprueba o rechaza. Si aprueba, la solicitud pasa a AUTORIZADO.',
    },
    {
      estado: 'AUTORIZADO',
      color: 'bg-green-100 border-green-300 text-green-800',
      modulo: '—',
      quien: 'Sistema',
      desc: 'Crédito aprobado. Se puede proceder a generar el contrato.',
    },
    {
      estado: 'CONTRATO IMPRESO',
      color: 'bg-teal-100 border-teal-300 text-teal-800',
      modulo: 'Imprimir Contrato',
      quien: 'Operativo',
      desc: 'Se genera e imprime el contrato del crédito. Incluye tabla de amortización y condiciones del crédito.',
    },
    {
      estado: 'FIRMAR CONTRATO',
      color: 'bg-teal-100 border-teal-300 text-teal-800',
      modulo: 'Firmar Contrato',
      quien: 'Cliente + Operativo',
      desc: 'El cliente firma el contrato. Se sube el documento firmado al sistema.',
    },
    {
      estado: 'POR FONDEAR',
      color: 'bg-indigo-100 border-indigo-300 text-indigo-800',
      modulo: 'Por Fondear / Transferencias',
      quien: 'Supervisor Contable',
      desc: 'Se valida el contrato firmado (Ok) y se prepara la transferencia bancaria al cliente.',
    },
    {
      estado: 'FONDEADO',
      color: 'bg-green-200 border-green-400 text-green-900',
      modulo: 'Fondeados',
      quien: 'Sistema',
      desc: 'Crédito dispersado. El dinero ya fue transferido al cliente. Pasa a Cobranza para seguimiento de pagos.',
    },
  ];

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600 mb-4">
        La solicitud avanza por etapas definidas. <strong>En cualquier etapa puede pasar a RECHAZADO.</strong> El
        cambio de estatus se registra en UpdateLog para auditoría completa.
      </p>

      <div className="relative">
        {estados.map((e, i) => (
          <div key={e.estado} className="flex items-start gap-4 mb-2">
            <div className="flex flex-col items-center">
              <div className={`w-4 h-4 rounded-full border-2 mt-3 flex-shrink-0 ${e.color.includes('green-200') ? 'border-green-500 bg-green-500' : 'border-gray-400 bg-white'}`} />
              {i < estados.length - 1 && <div className="w-0.5 h-full bg-gray-200 mt-1 flex-shrink-0" style={{ minHeight: '24px' }} />}
            </div>
            <div className={`flex-1 border rounded-lg p-3 mb-1 ${e.color}`}>
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <span className="font-bold text-sm">{e.estado}</span>
                <span className="text-xs opacity-75">· Módulo: {e.modulo}</span>
                <span className="text-xs opacity-75">· Responsable: {e.quien}</span>
              </div>
              <p className="text-xs leading-relaxed opacity-90">{e.desc}</p>
            </div>
          </div>
        ))}
        <div className="flex items-start gap-4">
          <div className="w-4 h-4 rounded-full border-2 border-red-400 bg-red-100 mt-3 flex-shrink-0" />
          <div className="flex-1 border border-red-300 bg-red-50 rounded-lg p-3">
            <p className="font-bold text-sm text-red-800">RECHAZADO (cualquier etapa)</p>
            <p className="text-xs text-red-700 mt-1">La solicitud puede ser rechazada en cualquier momento del flujo. Se registra el motivo y la fecha de rechazo.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Tab 4: Roles y Permisos ──────────────────────────────────────────────────

function TabRolesPermisos() {
  const roles = [
    { rol: 'sysAdmin', scope: 'all', desc: 'Acceso total. Gestión de usuarios, ACL, backups, configuración del sistema.' },
    { rol: 'gerenteComercial', scope: 'all', desc: 'Supervisión del proceso comercial, asesores y empresas.' },
    { rol: 'gerenteCartera', scope: 'all', desc: 'Supervisión de cartera activa, fondeados y cobranza.' },
    { rol: 'gerenteRiesgos', scope: 'all', desc: 'Autorización de créditos, revisión de análisis y riesgos.' },
    { rol: 'analistaRiesgos', scope: 'all', desc: 'Análisis crediticio detallado: revisión documental, pre-análisis, VoBos.' },
    { rol: 'asesor', scope: 'self', desc: 'Captura y seguimiento de sus propias solicitudes. Solo ve sus clientes.' },
    { rol: 'oficialDeCumplimiento', scope: 'all_limited', desc: 'Revisión de cumplimiento regulatorio (ROIPs, políticas, expedientes).' },
    { rol: 'gestorCobranza', scope: 'all_limited', desc: 'Seguimiento de pagos y gestión de cartera vencida.' },
    { rol: 'supervisorContable', scope: 'all', desc: 'Supervisión contable, transferencias y control de fondeados.' },
    { rol: 'analistaCumplimiento', scope: 'all_limited', desc: 'Análisis de cumplimiento regulatorio y reportes.' },
  ];

  return (
    <div className="space-y-4">
      <SectionTitle>Roles del Sistema</SectionTitle>
      <InfoTable
        headers={['Rol', 'Scope', 'Descripción']}
        rows={roles.map((r) => [
          <code key={r.rol} className="text-xs bg-gray-100 px-1.5 py-0.5 rounded font-mono">{r.rol}</code>,
          <span key={r.scope} className={`text-xs px-2 py-0.5 rounded font-medium ${r.scope === 'all' ? 'bg-green-100 text-green-700' : r.scope === 'self' ? 'bg-blue-100 text-blue-700' : 'bg-yellow-100 text-yellow-700'}`}>{r.scope}</span>,
          r.desc,
        ])}
      />

      <SectionTitle>Scopes de Acceso</SectionTitle>
      <InfoTable
        headers={['Scope', 'Comportamiento']}
        rows={[
          ['self', 'El usuario solo ve solicitudes donde owner_id coincide con su propio owner_id.'],
          ['all', 'El usuario ve todas las solicitudes del sistema sin restricción.'],
          ['all_limited', 'Ve todas las solicitudes pero con campos sensibles ocultados (forbidden_fields).'],
        ]}
      />

      <SectionTitle>Sistema ACL (Access Control List)</SectionTitle>
      <p className="text-sm text-gray-600 mb-3">
        El ACL funciona con una lista de <strong>forbidden_fields</strong>: menús y campos que el rol <em>no puede ver</em>.
        Al iniciar sesión, el frontend carga esta lista y filtra dinámicamente el menú lateral.
      </p>
      <InfoTable
        headers={['Concepto', 'Descripción']}
        rows={[
          ['forbidden_fields', 'Array de strings con los values de menú/campos prohibidos (ej: ["menuBackUps", "menuACL"])'],
          ['Filtrado en Sidebar', 'isMenuAllowed(value) devuelve false si el value está en forbidden_fields → el ítem desaparece del menú'],
          ['Protección por URL', 'Si el usuario accede directo a /backups y no tiene permiso → pantalla "Acceso Denegado"'],
          ['requiresACL', 'Algunas páginas requieren rol específico (ej: "sysAdmin"). Se verifica además del forbidden_fields'],
          ['Edición', 'El módulo ACL (/acl) permite al sysAdmin editar los forbidden_fields de cada rol en formato JSON'],
        ]}
      />

      <SectionTitle>Flujo de Autenticación</SectionTitle>
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-700 space-y-1">
        <p><strong>1. Login:</strong> POST /api/v1/auth/login con <code className="bg-gray-200 px-1 rounded">{'{ correo, password }'}</code></p>
        <p><strong>2. Hash:</strong> El backend valida <code className="bg-gray-200 px-1 rounded">bcrypt(SHA512(password+correo)[:72])</code> contra el campo <code className="bg-gray-200 px-1 rounded">hash</code>. Soporta 4 formatos legacy para compatibilidad.</p>
        <p><strong>3. JWT:</strong> Se genera token HS256 firmado con <code className="bg-gray-200 px-1 rounded">SECRET_KEY</code>, expira en 24 horas. Payload: <code className="bg-gray-200 px-1 rounded">{'{ sub, email, name, acl, exp }'}</code>.</p>
        <p><strong>4. Cookie:</strong> El token se guarda en cookie <code className="bg-gray-200 px-1 rounded">auth_token</code> (no HttpOnly, para que <code className="bg-gray-200 px-1 rounded">apiClient.ts</code> pueda añadirlo como <code className="bg-gray-200 px-1 rounded">Authorization: Bearer</code>).</p>
        <p><strong>5. ACL:</strong> El ACL viaja dentro del JWT; el frontend también llama a <code className="bg-gray-200 px-1 rounded">/api/v1/acl/by-name/{'{acl}'}</code> para cargar los forbidden_fields actualizados.</p>
        <p><strong>6. Menú:</strong> El sidebar filtra los módulos según forbidden_fields. El middleware valida expiración en cada request.</p>
        <p><strong>7. Datos sensibles:</strong> Al leer/escribir CLABE y nombres del cliente, el backend los cifra/descifra con <strong>Fernet</strong> usando <code className="bg-gray-200 px-1 rounded">FIELD_ENCRYPTION_KEY</code>. El frontend solo ve texto plano.</p>
      </div>
    </div>
  );
}

// ─── Tab 5: Referencia API ────────────────────────────────────────────────────

type BackendStatus = 'checking' | 'online' | 'offline';

function TabReferenciaAPI({ backendUrl }: { backendUrl: string }) {
  const scalarUrl  = `${backendUrl}/docs`;
  const openapiUrl = `${backendUrl}/openapi.json`;

  const [status, setStatus]   = useState<BackendStatus>('checking');
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [openapiMeta, setOpenapiMeta] = useState<{ title?: string; version?: string; paths?: number }>({});
  const [retryTick, setRetryTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setStatus('checking');
    setErrorMsg('');

    // AbortController para evitar que requests colgados bloqueen el UI
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);

    fetch(openapiUrl, {
      method: 'GET',
      credentials: 'include',
      signal: controller.signal,
    })
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const spec = await r.json();
        if (cancelled) return;
        setOpenapiMeta({
          title:   spec?.info?.title,
          version: spec?.info?.version,
          paths:   spec?.paths ? Object.keys(spec.paths).length : undefined,
        });
        setStatus('online');
      })
      .catch((err) => {
        if (cancelled) return;
        setErrorMsg(err?.name === 'AbortError' ? 'Tiempo de espera agotado (8 s).' : (err?.message || 'Error de red'));
        setStatus('offline');
      })
      .finally(() => {
        clearTimeout(timeout);
      });

    return () => {
      cancelled = true;
      controller.abort();
      clearTimeout(timeout);
    };
  }, [openapiUrl, retryTick]);

  const statusBadge =
    status === 'checking' ? (
      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 bg-gray-100 px-2.5 py-1 rounded-full">
        <span className="inline-block w-2 h-2 rounded-full bg-gray-400 animate-pulse" />
        Verificando…
      </span>
    ) : status === 'online' ? (
      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-green-700 bg-green-100 px-2.5 py-1 rounded-full">
        <span className="inline-block w-2 h-2 rounded-full bg-green-500" />
        Backend en línea
      </span>
    ) : (
      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-red-700 bg-red-100 px-2.5 py-1 rounded-full">
        <span className="inline-block w-2 h-2 rounded-full bg-red-500" />
        Backend no responde
      </span>
    );

  return (
    <div className="space-y-6">
      {/* Tarjeta de Scalar */}
      <div className="rounded-xl border border-teal-200 bg-gradient-to-br from-teal-50 to-white p-5">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-4">
          <div className="flex items-start gap-3">
            <div className="p-2.5 bg-white rounded-lg shadow-sm border border-teal-100">
              <Icon name="book" size={22} color="#0196A1" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <h3 className="font-semibold text-gray-800">Documentación API Interactiva (Scalar)</h3>
                {statusBadge}
              </div>
              <p className="text-xs text-gray-500">
                Endpoint:&nbsp;
                <code className="bg-white px-1.5 py-0.5 rounded border border-gray-200 text-gray-700">{scalarUrl}</code>
              </p>
              {status === 'online' && openapiMeta.title && (
                <p className="text-xs text-gray-500 mt-1">
                  {openapiMeta.title} · v{openapiMeta.version}
                  {typeof openapiMeta.paths === 'number' && <> · <strong>{openapiMeta.paths}</strong> paths</>}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setRetryTick((x) => x + 1)}
              disabled={status === 'checking'}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-white text-gray-700 border border-gray-200 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              <Icon name="refresh" size={14} />
              Reintentar
            </button>
            <a
              href={scalarUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-disabled={status !== 'online'}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                status === 'online'
                  ? 'bg-teal-600 text-white hover:bg-teal-700'
                  : 'bg-gray-200 text-gray-500 cursor-not-allowed'
              }`}
              onClick={(e) => { if (status !== 'online') e.preventDefault(); }}
            >
              <Icon name="transfer" size={16} />
              Abrir Scalar en pestaña nueva
            </a>
          </div>
        </div>

        {/* Mensaje de error concreto cuando está offline */}
        {status === 'offline' && (
          <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-800 leading-relaxed">
            <strong>No se pudo conectar al backend.</strong> Detalle:&nbsp;
            <code className="bg-white px-1 rounded border border-red-200">{errorMsg || 'sin detalles'}</code>.
            <div className="mt-2">Posibles causas:</div>
            <ul className="list-disc ml-5 mt-1 space-y-0.5">
              <li>El backend no está en marcha (<code className="bg-white px-1 rounded border border-red-200">docker compose up -d backend</code>).</li>
              <li><code className="bg-white px-1 rounded border border-red-200">NEXT_PUBLIC_API_URL</code> apunta a una URL incorrecta — actualmente <code className="bg-white px-1 rounded border border-red-200">{backendUrl}</code>.</li>
              <li>CORS del backend no incluye este origen (<code className="bg-white px-1 rounded border border-red-200">FRONTEND_URL</code> / <code className="bg-white px-1 rounded border border-red-200">CORS_ORIGINS</code>).</li>
              <li>En producción <code className="bg-white px-1 rounded border border-red-200">docs_url=None</code> — Scalar está deshabilitada. Usa la tabla estática de abajo.</li>
              <li>Mixed content: frontend en HTTPS intentando hablar con backend HTTP.</li>
            </ul>
          </div>
        )}
      </div>

      <div>
        <SectionTitle>Tabla Completa de Endpoints</SectionTitle>
        <p className="text-xs text-gray-500 mb-4">
          Hash de contraseña: <code className="bg-gray-100 px-1 rounded">bcrypt(SHA512(password+correo)[:72])</code> ·
          Auth: cookie <code className="bg-gray-100 px-1 rounded">auth_token</code> (JWT HS256 · 24 h) + espejo en <code className="bg-gray-100 px-1 rounded">Authorization: Bearer</code> ·
          Prefijo global: <code className="bg-gray-100 px-1 rounded">/api/v1</code> ·
          Rate limiting: <code className="bg-gray-100 px-1 rounded">slowapi</code> (login: 5/min) ·
          Datos sensibles cifrados con <strong>Fernet</strong> (<code className="bg-gray-100 px-1 rounded">FIELD_ENCRYPTION_KEY</code>)
        </p>
        {ENDPOINT_GROUPS.map((group) => (
          <div key={group.module} className="mb-6">
            <h4 className="text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-teal-500 inline-block" />
              {group.module}
              <code className="text-xs text-gray-400 font-normal">#{group.tag}</code>
            </h4>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="text-left px-2 py-1.5 font-semibold text-gray-600 border border-gray-200 w-16">Método</th>
                    <th className="text-left px-2 py-1.5 font-semibold text-gray-600 border border-gray-200 w-80">Endpoint</th>
                    <th className="text-left px-2 py-1.5 font-semibold text-gray-600 border border-gray-200">Descripción</th>
                  </tr>
                </thead>
                <tbody>
                  {group.endpoints.map((ep, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-2 py-1.5 border border-gray-200">
                        <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${METHOD_COLORS[ep.method]}`}>{ep.method}</span>
                      </td>
                      <td className="px-2 py-1.5 border border-gray-200 font-mono text-gray-700">{ep.path}</td>
                      <td className="px-2 py-1.5 border border-gray-200 text-gray-600">{ep.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Tab 6: Parámetros de Negocio ─────────────────────────────────────────────

function TabParametricos() {
  return (
    <div className="space-y-2">
      <p className="text-xs text-gray-500 mb-4">
        Fuente: <code className="bg-gray-100 px-1 rounded">mipymex-caabsa-backend/core/parametricos.py</code>
      </p>

      <SectionTitle>Factor de Capacidad de Pago</SectionTitle>
      <InfoTable
        headers={['Tipo de Empleado', 'Factor', 'Significado']}
        rows={[
          ['Sindicalizado', '1.0', '100% del sueldo neto disponible para el crédito'],
          ['No Sindicalizado', '0.30', '30% del sueldo neto disponible para el crédito'],
        ]}
      />

      <SectionTitle>Tasas Base por Producto</SectionTitle>
      <InfoTable
        headers={['Tipo de Crédito', 'Tasa Anual', 'Comisión']}
        rows={[
          ['Nómina', '45.0%', '3.0%'],
          ['Adelanto de Nómina', '0.0%', '6.0%'],
        ]}
      />

      <SectionTitle>Frecuencias de Pago</SectionTitle>
      <InfoTable
        headers={['Frecuencia', 'Pagos por año']}
        rows={[
          ['Semanal', '52'],
          ['Quincenal', '24'],
          ['Catorcenal', '24'],
          ['Mensual', '12'],
        ]}
      />

      <SectionTitle>Costo de Seguros por Monto de Póliza</SectionTitle>
      <InfoTable
        headers={['Monto Póliza', 'Costo por parcialidad']}
        rows={[
          ['$25,000', '$44.50'],
          ['$50,000', '$79.50'],
          ['$75,000', '$119.50'],
          ['$100,000', '$144.50'],
        ]}
      />

      <SectionTitle>Buró Crediticio BUENO — Clientes Internos / Externos</SectionTitle>
      <InfoTable
        headers={['Tipo', 'Antigüedad mín.', 'Tasa', 'Comisión', 'Meses Sueldo', 'Plazos disponibles']}
        rows={[
          ['Internos', '6 meses',  '40.20%', '3%', '1',  '6'],
          ['Internos', '12 meses', '36.00%', '3%', '2',  '6, 12, 18'],
          ['Internos', '24 meses', '31.80%', '3%', '3',  '6, 12, 18, 24, 30'],
          ['Internos', '48 meses', '28.20%', '3%', '4',  '6, 12, 18, 24, 30, 36'],
          ['Externos', '6 meses',  '40.80%', '3%', '1',  '6'],
          ['Externos', '12 meses', '37.20%', '3%', '2',  '6, 12, 18'],
          ['Externos', '24 meses', '32.40%', '3%', '3',  '6, 12, 18, 24, 30'],
          ['Externos', '48 meses', '28.80%', '3%', '4',  '6, 12, 18, 24, 30, 36'],
        ]}
      />

      <SectionTitle>Buró Crediticio BUENO — Directivos</SectionTitle>
      <InfoTable
        headers={['Tipo', 'Antigüedad mín.', 'Tasa', 'Comisión', 'Meses Sueldo', 'Plazos']}
        rows={[
          ['Dir. Interno', '6 meses',  '24.00%', '1%', '1',  '6, 12, 18'],
          ['Dir. Interno', '12 meses', '22.80%', '1%', '3',  '6, 12, 18, 24, 30'],
          ['Dir. Interno', '24 meses', '22.20%', '1%', '4',  '6, 12, 18, 24, 30, 36'],
          ['Dir. Interno', '48 meses', '21.60%', '1%', '5',  '6, 12, 18, 24, 30, 36'],
          ['Dir. Externo', '6 meses',  '24.00%', '1%', '1',  '6, 12, 18'],
          ['Dir. Externo', '12 meses', '23.40%', '1%', '3',  '6, 12, 18, 24, 30'],
          ['Dir. Externo', '24 meses', '22.80%', '1%', '4',  '6, 12, 18, 24, 30, 36'],
          ['Dir. Externo', '48 meses', '22.20%', '1%', '5',  '6, 12, 18, 24, 30, 36'],
        ]}
      />

      <SectionTitle>Buró Crediticio BUENO — Auxiliares y Choferes CAABSA/Eagle</SectionTitle>
      <InfoTable
        headers={['Tipo', 'Antigüedad mín.', 'Tasa', 'Comisión', 'Monto Máx.', 'Plazos']}
        rows={[
          ['Auxiliares', '12 meses', '35.04%', '3%', '$12,000', '6–12, 18'],
          ['Auxiliares', '24 meses', '32.64%', '2%', '$17,000', '6–12, 18, 24, 30'],
          ['Auxiliares', '36 meses', '32.64%', '2%', '$20,000', '6–12, 18, 24, 30'],
          ['Auxiliares', '48 meses', '26.64%', '1%', '$22,000', '6–12, 18, 24, 30, 36'],
          ['Auxiliares', '60 meses', '26.64%', '1%', '$27,000', '6–12, 18, 24, 30, 36'],
          ['Auxiliares', '72 meses', '26.64%', '1%', '$30,000', '6–12, 18, 24, 30, 36, 42'],
          ['Choferes',   '12 meses', '35.04%', '3%', '$14,000', '6–12, 18'],
          ['Choferes',   '24 meses', '32.64%', '2%', '$22,000', '6–12, 18, 24, 30'],
          ['Choferes',   '36 meses', '32.64%', '2%', '$24,000', '6–12, 18, 24, 30'],
          ['Choferes',   '48 meses', '26.64%', '1%', '$26,000', '6–12, 18, 24, 30, 36'],
        ]}
      />

      <SectionTitle>Buró Crediticio REGULAR — Internos / Externos</SectionTitle>
      <InfoTable
        headers={['Tipo', 'Antigüedad mín.', 'Tasa', 'Comisión', 'Meses Sueldo', 'Plazos']}
        rows={[
          ['Internos', '6 meses',  '45.00%', '3%', '0.5', '6'],
          ['Internos', '12 meses', '43.80%', '3%', '1',   '6, 12'],
          ['Internos', '24 meses', '42.60%', '3%', '2',   '6, 12, 18, 24'],
          ['Internos', '48 meses', '41.40%', '3%', '2',   '6, 12, 18, 24'],
          ['Externos', '6 meses',  '45.60%', '3%', '0.5', '6'],
          ['Externos', '12 meses', '44.40%', '3%', '1',   '6, 12'],
          ['Externos', '24 meses', '43.20%', '3%', '2',   '6, 12, 18, 24'],
          ['Externos', '48 meses', '42.00%', '3%', '2',   '6, 12, 18, 24'],
        ]}
      />

      <SectionTitle>Buró Crediticio REGULAR — Auxiliares y Choferes CAABSA/Eagle</SectionTitle>
      <InfoTable
        headers={['Tipo', 'Antigüedad mín.', 'Tasa', 'Comisión', 'Monto Máx.', 'Plazos']}
        rows={[
          ['Auxiliares', '12 meses', '47.04%', '6%', '$6,000',  '6–12'],
          ['Auxiliares', '24 meses', '44.64%', '4%', '$13,000', '6–12, 18, 24'],
          ['Auxiliares', '36 meses', '44.64%', '4%', '$15,000', '6–12, 18, 24'],
          ['Auxiliares', '48 meses', '38.64%', '3%', '$17,000', '6–12, 18, 24'],
          ['Auxiliares', '60 meses', '38.64%', '3%', '$22,000', '6–12, 18, 24, 30, 36'],
          ['Auxiliares', '72 meses', '38.64%', '3%', '$25,000', '6–12, 18, 24, 30, 36, 42'],
          ['Choferes',   '12 meses', '47.04%', '6%', '$7,000',  '6–12'],
          ['Choferes',   '24 meses', '44.64%', '4%', '$16,000', '6–12, 18, 24'],
          ['Choferes',   '36 meses', '44.64%', '4%', '$19,000', '6–12, 18, 24'],
          ['Choferes',   '48 meses', '38.64%', '3%', '$21,000', '6–12, 18, 24'],
        ]}
      />

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 mt-4">
        <strong>Nota:</strong> La empresa <code className="bg-amber-100 px-1 rounded">C.A.E</code> tiene excepción a la regla de 6 meses de antigüedad.
        Si el cliente tiene antigüedad ≥ 3 meses, se permite con plazo = 6 meses.
      </div>
    </div>
  );
}

// ─── Tab 7: Guía Técnica ──────────────────────────────────────────────────────

function TabGuiaTecnica() {
  return (
    <div className="space-y-2">
      <SectionTitle>Variables de Entorno — Backend</SectionTitle>
      <CodeBlock>{`# mipymex-caabsa-backend/config.example.env
POSTGRES_USER=postgres
POSTGRES_PASSWORD=admin              # NUNCA en repo — usar secret store en prod
POSTGRES_HOST=127.0.0.1
POSTGRES_PORT=5434                   # 5432 en servicios gestionados (Cloud SQL/RDS/Azure)
POSTGRES_DB=my_pymex

# Autenticación (JWT)
SECRET_KEY=dev-secret-key            # >= 64 bytes aleatorios en producción
                                     # Rotarla invalida TODAS las sesiones activas

# Encriptación de campos sensibles en BD (Fernet)
# Protege CLABE y nombres de cliente en 'solicitudes', api_key en 'users'
# Genera con: python scripts/generate_encryption_key.py
FIELD_ENCRYPTION_KEY=                # ⚠️ Si se pierde, los datos cifrados NO son recuperables

# Entorno
ENV=development                      # "development" habilita logs DEBUG y docs
FRONTEND_URL=http://localhost:3000
CORS_ORIGIN=                         # URL extra del frontend en producción
CORS_ORIGINS=                        # Varios orígenes separados por coma`}</CodeBlock>

      <SectionTitle>Variables de Entorno — Frontend</SectionTitle>
      <CodeBlock>{`# mipymex-caabsa-frontend/.env.local
NEXT_PUBLIC_API_URL=http://localhost:8000/api/v1   # Si no se setea, usa este default
NODE_ENV=development`}</CodeBlock>

      <SectionTitle>Comandos de Desarrollo</SectionTitle>
      <CodeBlock>{`# ─── Backend ────────────────────────────────────────────────
cd mipymex-caabsa-backend
python3 -m venv venv && source venv/bin/activate
pip install -r requirements.txt
POSTGRES_USER=postgres POSTGRES_PASSWORD=admin POSTGRES_HOST=127.0.0.1 \\
  POSTGRES_PORT=5434 POSTGRES_DB=my_pymex SECRET_KEY="dev-secret-key" \\
  uvicorn main:app --host 0.0.0.0 --port 8000 --reload

# ─── Frontend ────────────────────────────────────────────────
nvm use 20   # Next.js 16 requiere Node >= 20.9.0
cd mipymex-caabsa-frontend
npm install
npm run dev          # Servidor en puerto 3000
npm run build        # Build de producción
npm run lint         # ESLint
npm run type-check   # TypeScript (tsc --noEmit)

# ─── Docker (ambos servicios) ────────────────────────────────
cd mipymex-caabsa-frontend
docker compose up                   # Levanta backend + frontend
docker compose up -d backend        # Solo backend en background
docker compose restart backend      # Si cambios no se aplican
docker compose logs -f backend      # Ver logs del backend
docker compose ps                   # Estado de los servicios`}</CodeBlock>

      <SectionTitle>Problemas Conocidos y Soluciones</SectionTitle>
      <InfoTable
        headers={['Problema', 'Causa', 'Solución']}
        rows={[
          ['"El servidor no respondió a tiempo"', 'Backend no levantado o PostgreSQL caído', 'docker compose up -d backend; verificar que PostgreSQL esté en puerto 5434'],
          ['Permisos EACCES en frontend', 'Docker crea archivos como root', 'sudo chown -R $USER:$USER mipymex-caabsa-frontend/'],
          ['Error de versión de Node', 'Sistema tiene Node 18, se necesita >= 20.9.0', 'nvm use 20 (instalar con nvm install 20 si no existe)'],
          ['Backend no inicia (DB error)', 'Faltan variables POSTGRES_*', 'Verificar que todas las variables de entorno estén configuradas'],
          ['Cambios en código no aplican', 'Docker no detecta cambios', 'docker compose restart backend o docker compose restart frontend'],
          ['API docs no disponibles en prod', 'docs_url=None en producción', 'Acceder localmente al backend (localhost:8000/docs) o usar esta guía'],
        ]}
      />

      <SectionTitle>Despliegue en Producción (resumen)</SectionTitle>
      <p className="text-xs text-gray-500 mb-3">Ver detalle completo de AWS / GCP / Azure en la pestaña <strong>Despliegue Cloud</strong>.</p>
      <InfoTable
        headers={['Componente', 'Plataforma', 'Detalles']}
        rows={[
          ['Backend', 'Cloud Run · ECS Fargate · Container Apps', 'Imagen Docker Python 3.11-slim → uvicorn en puerto 8000'],
          ['Frontend', 'Cloud Run · App Runner · Container Apps', 'Imagen Docker Node 20-alpine → Next.js standalone en puerto 3000'],
          ['Base de Datos', 'Cloud SQL · RDS · Azure PostgreSQL Flexible Server', 'PostgreSQL 16 · pool_size=3 · max_overflow=5 · pool_recycle=900s'],
          ['Secretos', 'Secret Manager · Secrets Manager · Key Vault', 'SECRET_KEY · FIELD_ENCRYPTION_KEY · POSTGRES_PASSWORD'],
          ['Build', 'Cloud Build · CodeBuild · Azure DevOps', 'cloudbuild.yaml / GitHub Actions / Azure Pipelines'],
          ['Auto-migración', 'Al iniciar el backend', 'Columnas nuevas en modelos se agregan automáticamente (sin Alembic manual)'],
          ['API Docs', 'Deshabilitadas en prod', 'docs_url=None, redoc_url=None. Usar este módulo de Ayuda.'],
        ]}
      />

      <SectionTitle>Convenciones de Código</SectionTitle>
      <InfoTable
        headers={['Área', 'Convención']}
        rows={[
          ['Backend (Python)', 'snake_case en variables y funciones, dependency injection con Depends(), session-per-request'],
          ['Frontend (TypeScript)', 'camelCase, componentes funcionales, Tailwind CSS + estilos en src/styles/'],
          ['API aliases', 'Modelos usan snake_case internamente pero exponen camelCase via aliases Pydantic (ej: owner_id → ownerID)'],
          ['Color primario', '#0196A1 (teal) en toda la UI'],
          ['Iconos', 'lucide-react (componente Icon centralizado en src/components/ui/icons/)'],
          ['Gráficas', 'Recharts'],
          ['PDFs', 'jsPDF'],
          ['Idioma', 'UI y variables de negocio en español'],
          ['Estado', 'Solo React hooks (useState, useEffect, custom hooks). Sin Redux/Zustand. Sin caching (refetch en cada navegación)'],
        ]}
      />

      <SectionTitle>Archivos Clave del Proyecto</SectionTitle>
      <InfoTable
        headers={['Archivo', 'Propósito']}
        rows={[
          ['backend/main.py', 'Entry point FastAPI: CORS dinámico, SecurityHeadersMiddleware, slowapi rate limit, Scalar docs'],
          ['backend/core/database.py', 'Engine SQLAlchemy, pool tunning para Cloud Run, auto-migración de columnas'],
          ['backend/core/encryption.py', 'Fernet + EncryptedString (TypeDecorator) — cifrado transparente a nivel de columna'],
          ['backend/core/auth.py', 'SECRET_KEY, ALGORITHM=HS256, get_current_user dependency'],
          ['backend/core/parametricos.py', 'Tasas, comisiones, plazos y factores de negocio (fuente de verdad)'],
          ['backend/services/auth_service.py', 'hash_password_bcrypt, verify_password (4 formatos legacy), create_access_token'],
          ['backend/services/solicitud/', 'Servicio más complejo: 7 mixins (crud, queries, análisis, cálculos, propuestas, state_machine, helpers)'],
          ['backend/api/v1/api.py', 'Agregador de los 21 routers con sus prefijos'],
          ['backend/scripts/generate_encryption_key.py', 'Genera la clave Fernet (secret.key) con cryptography.Fernet.generate_key()'],
          ['frontend/src/config/dashboardPages.ts', 'SSOT de las 43 páginas del menú (nombre, ícono, componente, URL, ACL requerido)'],
          ['frontend/src/lib/api/apiClient.ts', 'Wrapper central HTTP: base URL, credentials:"include", HTTPS forcing, Pydantic error handling'],
          ['frontend/src/utils/fieldVisibility.ts', 'Visibilidad de campos por módulo (18 módulos) y etapa del flujo'],
          ['frontend/src/hooks/useAuth.ts', 'Estado de autenticación + permisos ACL (isMenuAllowed, isFieldAllowed, forbidden_fields)'],
          ['frontend/middleware.ts', 'Middleware Edge: valida cookie auth_token y redirige a /login si expirado'],
          ['frontend/next.config.js', 'output: "standalone", security headers (X-Frame-Options, HSTS, etc.)'],
          ['frontend/cloudbuild.yaml', 'Pipeline GCP Cloud Build: build con NEXT_PUBLIC_API_URL, push a Artifact Registry, deploy a Cloud Run'],
        ]}
      />
    </div>
  );
}

// ─── Tab 8: Despliegue Cloud (AWS · GCP · Azure) ──────────────────────────────

function TabDespliegueCloud() {
  return (
    <div className="space-y-2">
      <p className="text-sm text-gray-600 mb-4">
        La plataforma está contenerizada con Docker y es <strong>portable entre las tres nubes principales</strong>.
        La imagen Docker es la misma en AWS, GCP y Azure — solo cambia cómo se inyectan los secretos y la URL del backend
        (que se compila en build-time en el caso del frontend).
      </p>

      <SectionTitle>Comparativa de Servicios por Proveedor</SectionTitle>
      <InfoTable
        headers={['Categoría', 'Google Cloud Platform', 'Amazon Web Services', 'Microsoft Azure']}
        rows={[
          ['Cómputo backend/frontend', 'Cloud Run (serverless)', 'ECS Fargate · App Runner', 'Container Apps · App Service'],
          ['Base de datos', 'Cloud SQL PostgreSQL 16', 'RDS PostgreSQL 16', 'Azure DB for PostgreSQL Flexible Server 16'],
          ['Gestión de secretos', 'Secret Manager', 'AWS Secrets Manager', 'Azure Key Vault'],
          ['Container registry', 'Artifact Registry', 'ECR (Elastic Container Registry)', 'Azure Container Registry (ACR)'],
          ['CI/CD sugerido', 'Cloud Build (cloudbuild.yaml)', 'CodeBuild + CodePipeline · GH Actions', 'Azure DevOps · GitHub Actions'],
          ['CDN / Ingress', 'Cloud CDN + Load Balancer', 'CloudFront + ALB', 'Azure Front Door · Application Gateway'],
          ['HA / Multi-AZ', 'Regional failover automático', 'Multi-AZ deployment', 'Zone redundant HA'],
        ]}
      />

      <SectionTitle>Google Cloud Platform (GCP)</SectionTitle>
      <CodeBlock>{`# 1. Crear Cloud SQL PostgreSQL
gcloud sql instances create mipymex-db \\
    --database-version=POSTGRES_16 --tier=db-custom-2-4096 \\
    --region=us-central1 --storage-type=SSD --storage-size=20GB \\
    --backup --enable-point-in-time-recovery
gcloud sql databases create my_pymex --instance=mipymex-db
gcloud sql users create postgres --instance=mipymex-db --password=<pwd>

# 2. Guardar secretos en Secret Manager
printf '<valor>' | gcloud secrets create SECRET_KEY --data-file=-
printf '<valor>' | gcloud secrets create FIELD_ENCRYPTION_KEY --data-file=-
printf '<pwd>'   | gcloud secrets create POSTGRES_PASSWORD --data-file=-

# 3. Build & Deploy con pipeline Cloud Build
gcloud builds submit --config cloudbuild.yaml mipymex-caabsa-backend/
gcloud builds submit --config cloudbuild.yaml mipymex-caabsa-frontend/

# 4. Cloud Run (backend) con secretos montados
gcloud run deploy mipymex-caabsa-backend \\
    --image us-central1-docker.pkg.dev/$PROJECT/repo/backend:latest \\
    --region us-central1 --platform managed --allow-unauthenticated \\
    --add-cloudsql-instances=$PROJECT:us-central1:mipymex-db \\
    --set-env-vars="ENV=production,POSTGRES_HOST=/cloudsql/$PROJECT:us-central1:mipymex-db,POSTGRES_DB=my_pymex,POSTGRES_USER=postgres,POSTGRES_PORT=5432" \\
    --set-secrets="SECRET_KEY=SECRET_KEY:latest,FIELD_ENCRYPTION_KEY=FIELD_ENCRYPTION_KEY:latest,POSTGRES_PASSWORD=POSTGRES_PASSWORD:latest"`}</CodeBlock>

      <SectionTitle>Amazon Web Services (AWS)</SectionTitle>
      <CodeBlock>{`# 1. RDS PostgreSQL 16 en VPC privada
aws rds create-db-instance \\
    --db-instance-identifier mipymex-db \\
    --db-instance-class db.t3.small \\
    --engine postgres --engine-version 16 \\
    --allocated-storage 20 --storage-type gp3 --storage-encrypted \\
    --master-username postgres --master-user-password <pwd> \\
    --db-name my_pymex --vpc-security-group-ids sg-xxxx \\
    --backup-retention-period 7 --no-publicly-accessible

# 2. Secrets Manager
aws secretsmanager create-secret --name mipymex/SECRET_KEY           --secret-string "<valor>"
aws secretsmanager create-secret --name mipymex/FIELD_ENCRYPTION_KEY --secret-string "<valor>"
aws secretsmanager create-secret --name mipymex/POSTGRES_PASSWORD    --secret-string "<pwd>"

# 3. ECR (Elastic Container Registry)
aws ecr create-repository --repository-name mipymex/backend
docker build -t mipymex/backend ./mipymex-caabsa-backend
docker tag mipymex/backend:latest $ACCOUNT.dkr.ecr.$REGION.amazonaws.com/mipymex/backend:latest
aws ecr get-login-password | docker login --username AWS --password-stdin \\
    $ACCOUNT.dkr.ecr.$REGION.amazonaws.com
docker push $ACCOUNT.dkr.ecr.$REGION.amazonaws.com/mipymex/backend:latest

# 4. ECS Fargate — la Task Definition referencia los secretos por ARN (no los inyecta en claro):
#    "secrets": [{ "name": "SECRET_KEY",           "valueFrom": "arn:aws:secretsmanager:...:mipymex/SECRET_KEY" },
#                { "name": "FIELD_ENCRYPTION_KEY", "valueFrom": "arn:aws:secretsmanager:...:mipymex/FIELD_ENCRYPTION_KEY" },
#                { "name": "POSTGRES_PASSWORD",    "valueFrom": "arn:aws:secretsmanager:...:mipymex/POSTGRES_PASSWORD" }]
# Alternativa más simple para cargas moderadas: AWS App Runner (deploy directo desde ECR).`}</CodeBlock>

      <SectionTitle>Microsoft Azure</SectionTitle>
      <CodeBlock>{`# 1. PostgreSQL Flexible Server
az postgres flexible-server create \\
    --resource-group mipymex-rg --name mipymex-db --location eastus \\
    --admin-user postgres --admin-password <pwd> \\
    --sku-name Standard_B2s --tier Burstable \\
    --storage-size 32 --version 16
az postgres flexible-server db create -g mipymex-rg -s mipymex-db -d my_pymex

# 2. Key Vault
az keyvault create -g mipymex-rg -n mipymex-kv
az keyvault secret set --vault-name mipymex-kv --name SECRET-KEY           --value "<valor>"
az keyvault secret set --vault-name mipymex-kv --name FIELD-ENCRYPTION-KEY --value "<valor>"
az keyvault secret set --vault-name mipymex-kv --name POSTGRES-PASSWORD    --value "<pwd>"

# 3. ACR y Container Apps (con Managed Identity leyendo Key Vault)
az acr create -g mipymex-rg -n mipymexacr --sku Basic
az acr build -r mipymexacr -t backend:latest ./mipymex-caabsa-backend
az containerapp create \\
    --resource-group mipymex-rg --name mipymex-backend \\
    --environment mipymex-env \\
    --image mipymexacr.azurecr.io/backend:latest \\
    --target-port 8000 --ingress external \\
    --registry-server mipymexacr.azurecr.io \\
    --env-vars "ENV=production" "POSTGRES_HOST=mipymex-db.postgres.database.azure.com" \\
               "POSTGRES_DB=my_pymex" "POSTGRES_USER=postgres" "POSTGRES_PORT=5432" \\
               "SECRET_KEY=secretref:secret-key" \\
               "FIELD_ENCRYPTION_KEY=secretref:field-key" \\
               "POSTGRES_PASSWORD=secretref:pg-password"`}</CodeBlock>

      <SectionTitle>Configuración del Pool de Conexiones (common)</SectionTitle>
      <CodeBlock>{`# backend/core/database.py — la misma config funciona en las 3 nubes
engine = create_engine(
    DATABASE_URL,
    pool_size=3,          # Conexiones base (servicios stateless)
    max_overflow=5,       # Hasta 8 conexiones por instancia bajo presión
    pool_recycle=900,     # 15 min — los gestionados cierran conexiones idle
    pool_pre_ping=True,   # Healthcheck antes de usar la conexión
    connect_args={
        "connect_timeout": 10,
        "options": "-c statement_timeout=30000",   # > 30 s abort
    },
)`}</CodeBlock>

      <SectionTitle>Checklist de Producción Multi-Cloud</SectionTitle>
      <InfoTable
        headers={['#', 'Verificación']}
        rows={[
          ['1', 'ENV=production en todos los contenedores'],
          ['2', 'FIELD_ENCRYPTION_KEY, SECRET_KEY y POSTGRES_PASSWORD desde el secret store — nunca hardcoded'],
          ['3', 'Backup cifrado de la clave Fernet en bóveda offline (HSM · gestor corporativo · caja fuerte)'],
          ['4', 'secret.key fuera del repositorio (.gitignore ya lo incluye)'],
          ['5', 'PostgreSQL con sslmode=require y acceso restringido a la subred del cómputo'],
          ['6', 'TLS en el ingress (ALB · Cloud Run · Container Apps)'],
          ['7', 'Min. 1 réplica activa para evitar cold starts'],
          ['8', 'NEXT_PUBLIC_API_URL inyectado en build-time con la URL correcta de la nube destino'],
          ['9', 'CORS_ORIGINS del backend incluye el dominio del frontend de esa nube'],
          ['10', 'Rotación anual de SECRET_KEY · cada ~3 años de FIELD_ENCRYPTION_KEY (con MultiFernet)'],
        ]}
      />
    </div>
  );
}

// ─── Tab 9: Seguridad y Encriptación ──────────────────────────────────────────

function TabSeguridad() {
  return (
    <div className="space-y-2">
      <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 text-sm text-teal-900 mb-4">
        <strong>Modelo de seguridad en 3 capas:</strong> la plataforma protege datos en reposo (Fernet),
        sesiones (JWT HS256) y contraseñas (bcrypt). Además, todo viaja sobre TLS en tránsito y el proveedor
        cifra el disco de PostgreSQL con AES-256.
      </div>

      <SectionTitle>Capas Criptográficas</SectionTitle>
      <InfoTable
        headers={['Capa', 'Algoritmo', 'Clave', 'Qué protege']}
        rows={[
          ['Datos en columna (BD)', 'Fernet (AES-128-CBC + HMAC-SHA256)', 'FIELD_ENCRYPTION_KEY (44 chars base64)', 'CLABE, nombres del cliente (nombre + apellidos), api_key de usuarios'],
          ['Token de sesión', 'JWT HS256', 'SECRET_KEY (>= 64 bytes)', 'Cookie auth_token (expira en 24 h), payload {sub, email, name, acl, exp}'],
          ['Contraseña de usuario', 'bcrypt(SHA512(password+correo)[:72])', 'sal aleatoria por registro', 'Columna users.hash. Se aplica SHA512 previo porque bcrypt trunca > 72 bytes.'],
          ['Disco PostgreSQL', 'AES-256 (provider)', 'gestionada por Cloud SQL / RDS / Azure', 'Todo el volumen de la base de datos en reposo'],
          ['TLS en tránsito', 'TLS 1.2+', 'certificado del ingress', 'Todas las comunicaciones frontend ↔ backend ↔ BD'],
        ]}
      />

      <SectionTitle>Campos Cifrados Actualmente con Fernet</SectionTitle>
      <InfoTable
        headers={['Tabla', 'Columnas']}
        rows={[
          ['solicitudes', 'cliente_nombre, cliente_apellido_paterno, cliente_apellido_materno, cliente_clabe'],
          ['users', 'api_key (para integraciones externas)'],
        ]}
      />

      <SectionTitle>Cómo Funciona la Encriptación a Nivel de Columna</SectionTitle>
      <p className="text-sm text-gray-600 mb-3">
        <code className="bg-gray-100 px-1 rounded">core/encryption.py</code> define <code>EncryptedString</code>, un
        <code> TypeDecorator</code> de SQLAlchemy que cifra antes de INSERT/UPDATE y descifra después de SELECT.
        Todo el código de negocio sigue leyendo y escribiendo <em>strings normales</em> — la capa es transparente.
      </p>
      <InfoTable
        headers={['Comportamiento', 'Detalle']}
        rows={[
          ['Prefijo detector', 'Los tokens Fernet siempre empiezan con "gAAAA". Esto permite distinguirlos de datos en texto plano.'],
          ['Idempotente', 'Nunca re-cifra un valor ya cifrado (verifica el prefijo antes de encriptar).'],
          ['Backward-compatible', 'Los datos legacy en texto plano se retornan tal cual al leer (no rompe datos históricos).'],
          ['Tolerante a fallos', 'Si al desencriptar se lanza InvalidToken, loguea el error y devuelve el cifrado en lugar de romper la app.'],
          ['Sin cambios de esquema', 'La columna en PostgreSQL sigue siendo TEXT — los tokens caben sin modificaciones.'],
          ['None y "" sin modificar', 'Se guardan tal cual para que IS NULL y != "" sigan funcionando a nivel SQL.'],
        ]}
      />

      <SectionTitle>Orden de Búsqueda de la Clave Fernet</SectionTitle>
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-700 space-y-1">
        <p><strong>1. Variable de entorno</strong> <code className="bg-gray-200 px-1 rounded">FIELD_ENCRYPTION_KEY</code> — recomendado en la nube (secret store).</p>
        <p><strong>2. Archivo</strong> <code className="bg-gray-200 px-1 rounded">secret.key</code> en la raíz del backend — para desarrollo local.</p>
        <p><strong>3. Archivo</strong> <code className="bg-gray-200 px-1 rounded">mysite/secret.key</code> — ruta legacy heredada del sistema antiguo.</p>
        <p><strong>4. Clave temporal en memoria</strong> — <u>solo si ENV ≠ production</u>. Emite warning. Al reiniciar se pierden los datos cifrados con esa clave.</p>
        <p className="text-xs text-red-600 mt-2 font-semibold">⚠️ En producción (ENV=production) falla explícitamente si no encuentra la clave.</p>
      </div>

      <SectionTitle>Generar o Regenerar la Clave Fernet</SectionTitle>
      <CodeBlock>{`# Opción A — script incluido
cd mipymex-caabsa-backend
python scripts/generate_encryption_key.py
# → crea secret.key (44 chars base64) en la raíz del backend
# → secret.key ya está en .gitignore

# Opción B — manual
python -c "from cryptography.fernet import Fernet; print(Fernet.generate_key().decode())"
# → ej: "xM3kJ7aNpQrSt-vWxYz1234567890abcdefghijklmnop="`}</CodeBlock>

      <SectionTitle>Rotación de la Clave Fernet</SectionTitle>
      <p className="text-sm text-gray-600 mb-3">
        Rotar la clave es <strong>disruptivo</strong> — todos los datos cifrados con la clave vieja deben
        re-cifrarse con la nueva. Se usa <code className="bg-gray-100 px-1 rounded">MultiFernet</code> que
        lee con cualquiera de las dos claves y escribe con la primera.
      </p>
      <CodeBlock>{`# scripts/rotate_field_encryption_key.py (patrón)
from cryptography.fernet import Fernet, MultiFernet

old = Fernet(b"<clave_vieja>")
new = Fernet(b"<clave_nueva>")
mf  = MultiFernet([new, old])   # lee con cualquiera, escribe con la primera

# Para cada registro con columna encriptada:
#   1. Leer con MultiFernet (descifra con old o new)
#   2. Re-encriptar con new
#   3. Guardar

# Pasos operativos recomendados:
# a) Guardar la nueva clave en el secret store como versión nueva
# b) Activar ventana de mantenimiento (lectura read-only)
# c) Ejecutar el script iterando solicitudes y users
# d) Cambiar el binding de FIELD_ENCRYPTION_KEY al nuevo valor y reiniciar los contenedores
# e) Validar con queries de muestreo (descifrar aleatoriamente 20 registros)
# f) Después de 30 días sin incidencias, retirar la clave vieja del secret store`}</CodeBlock>

      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-800 mt-4">
        <strong>⚠️ CRÍTICO sobre la clave Fernet:</strong>
        <ul className="list-disc ml-5 mt-2 space-y-1">
          <li>Si la clave se <strong>pierde</strong>, los datos cifrados son <strong>irrecuperables por ningún medio</strong>. No existe backdoor.</li>
          <li>Respáldala en <strong>dos lugares</strong>: el secret store de la nube + una bóveda offline (HSM, gestor de contraseñas corporativo, sobre físico en caja fuerte).</li>
          <li>Nunca subas <code className="bg-red-100 px-1 rounded">secret.key</code> al repositorio. Ya está en <code className="bg-red-100 px-1 rounded">.gitignore</code>.</li>
          <li>No loguees datos cifrados ni la clave. Los handlers ya redactan campos sensibles.</li>
        </ul>
      </div>

      <SectionTitle>Autenticación: JWT HS256</SectionTitle>
      <InfoTable
        headers={['Propiedad', 'Valor']}
        rows={[
          ['Algoritmo', 'HS256 (HMAC-SHA256) firmado con SECRET_KEY'],
          ['Expiración', '24 horas desde el login'],
          ['Transporte', 'Cookie auth_token + espejo en Authorization: Bearer'],
          ['Payload', '{ sub: owner_id, email, name, acl: rol, exp: timestamp }'],
          ['Validación en backend', 'jwt.decode() en cada request protegido — verifica firma y exp'],
          ['Validación en frontend', 'middleware.ts decodifica exp y redirige a /login si expiró (Edge Runtime)'],
          ['Rotar SECRET_KEY', '⚠️ Invalida TODAS las sesiones activas — planear fuera de horario laboral'],
        ]}
      />

      <SectionTitle>Contraseñas: bcrypt(SHA512(password+correo)[:72])</SectionTitle>
      <p className="text-sm text-gray-600 mb-3">
        El hash se genera aplicando SHA512 previo (porque bcrypt trunca entradas mayores a 72 bytes) y luego bcrypt con sal aleatoria.
        <code className="bg-gray-100 px-1 rounded mx-1">verify_password()</code> soporta 4 formatos para compatibilidad histórica:
      </p>
      <InfoTable
        headers={['#', 'Formato', 'Uso']}
        rows={[
          ['1', 'bcrypt(SHA512(password+correo)[:72])', 'Estándar actual'],
          ['2', 'bcrypt(password)', 'Versión intermedia'],
          ['3', 'SHA512(password + correo) (sin bcrypt)', 'Legacy del sistema antiguo Flask'],
          ['4', 'Texto plano', 'Deprecado — solo para migración silenciosa (se re-hashea en primer login exitoso)'],
        ]}
      />

      <SectionTitle>Medidas Adicionales de Seguridad</SectionTitle>
      <InfoTable
        headers={['Medida', 'Implementación']}
        rows={[
          ['HTTPS obligatorio', 'Cloud Run / ALB / Container Apps terminan TLS. apiClient.ts fuerza https:// si detecta que el navegador está en HTTPS.'],
          ['Security headers (backend)', 'SecurityHeadersMiddleware en main.py: X-Frame-Options: DENY, X-Content-Type-Options: nosniff, Referrer-Policy, HSTS (solo prod).'],
          ['Security headers (frontend)', 'next.config.js añade los mismos headers + Permissions-Policy restrictiva.'],
          ['CORS dinámico', 'Orígenes leídos de FRONTEND_URL + CORS_ORIGINS. Middleware ASGI puro para no romper preflight OPTIONS.'],
          ['Rate limiting', 'slowapi con get_remote_address. Login: 5/min por IP.'],
          ['Auditoría', 'Tabla UpdateLog registra usuario, acción, tabla, fila, timestamp, detalles en cada CRUD sensible (~91K registros).'],
          ['Sanitización HTML', 'dompurify en frontend antes de renderizar contenido dinámico.'],
          ['Password never stored client', 'Se hashea SHA512 antes de enviar y se descarta. No se guarda en localStorage ni cookies.'],
          ['API docs restringidas', 'docs_url=None, redoc_url=None en producción. Scalar solo en staging.'],
          ['Edge validation del JWT', 'middleware.ts valida exp antes de permitir el request — ahorra roundtrip al backend cuando el token expiró.'],
        ]}
      />
    </div>
  );
}

// ─── Componente Principal ─────────────────────────────────────────────────────

const TABS = [
  { id: 'vision',       label: 'Visión General' },
  { id: 'modulos',      label: 'Módulos del Sistema' },
  { id: 'flujo',        label: 'Flujo de Solicitudes' },
  { id: 'roles',        label: 'Roles y Permisos' },
  { id: 'api',          label: 'Referencia API' },
  { id: 'parametricos', label: 'Parámetros de Negocio' },
  { id: 'despliegue',   label: 'Despliegue Cloud' },
  { id: 'seguridad',    label: 'Seguridad' },
  { id: 'tecnica',      label: 'Guía Técnica' },
];

const AyudaManagement: React.FC = () => {
  const [activeTab, setActiveTab]         = useState('vision');
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  const apiUrl    = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000/api/v1';
  const backendUrl = apiUrl.replace('/api/v1', '');

  const generatePDF = async () => {
    setIsGeneratingPDF(true);
    const savedTab = activeTab;

    try {
      const doc   = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const A4_W  = 210;
      const A4_H  = 297;
      const M     = 10;               // margen izquierdo/derecho
      const IMG_W = A4_W - 2 * M;    // 190 mm de ancho útil

      // ── Portada ────────────────────────────────────────────────────────────
      doc.setFontSize(26);
      doc.setTextColor(1, 150, 161);
      doc.text('Documentación Técnica', A4_W / 2, 88, { align: 'center' });
      doc.text('MiPymex CAABSA',        A4_W / 2, 102, { align: 'center' });
      doc.setDrawColor(1, 150, 161);
      doc.setLineWidth(0.5);
      doc.line(M + 20, 110, A4_W - M - 20, 110);
      doc.setFontSize(13);
      doc.setTextColor(90, 90, 90);
      doc.text('Plataforma de Gestión de Créditos y Préstamos', A4_W / 2, 122, { align: 'center' });
      doc.setFontSize(10);
      doc.setTextColor(150, 150, 150);
      doc.text(
        `Generado el ${new Date().toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' })}`,
        A4_W / 2, 136, { align: 'center' }
      );

      // ── Helper: distribuye imagen alta en varias páginas ─────────────────
      // Algoritmo correcto: la imagen se coloca a y = startY - i * A4_H,
      // de modo que el recorte natural del PDF muestra el tramo correcto
      // de cada página sin comprimir ni duplicar contenido.
      const addSplitImage = (imgData: string, imgH: number, startY: number) => {
        const numPages = Math.ceil((imgH + startY) / A4_H);
        for (let i = 0; i < numPages; i++) {
          if (i > 0) doc.addPage();
          doc.addImage(imgData, 'PNG', M, startY - i * A4_H, IMG_W, imgH);
        }
      };

      // ── Captura de cada tab ─────────────────────────────────────────────
      // Se omite el tab "api" porque contiene un iframe externo.
      const tabsToExport = [
        { id: 'vision',       label: 'Visión General' },
        { id: 'modulos',      label: 'Módulos del Sistema' },
        { id: 'flujo',        label: 'Flujo de Solicitudes' },
        { id: 'roles',        label: 'Roles y Permisos' },
        { id: 'parametricos', label: 'Parámetros de Negocio' },
        { id: 'despliegue',   label: 'Despliegue Cloud (AWS · GCP · Azure)' },
        { id: 'seguridad',    label: 'Seguridad y Encriptación' },
        { id: 'tecnica',      label: 'Guía Técnica' },
      ];

      // Y donde termina el encabezado del capítulo (título + línea divisoria)
      const CHAPTER_END_Y = 26;

      for (const tab of tabsToExport) {
        setActiveTab(tab.id);
        // Esperar suficiente para que React actualice el DOM
        await new Promise<void>(r => setTimeout(r, 400));

        // Primer hijo directo de .p-6 — el div raíz del componente de tab
        const el = document.querySelector('#ayuda-content .p-6 > div') as HTMLElement | null;
        if (!el) continue;

        const canvas = await html2canvas(el, {
          scale: 1.5,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff',
          scrollY: -window.scrollY,
        });

        const imgData = canvas.toDataURL('image/png');
        // Alto real de la imagen en mm (manteniendo proporción)
        const imgH = (canvas.height / canvas.width) * IMG_W;

        // Nueva página con título del capítulo (texto nativo jsPDF)
        doc.addPage();
        doc.setFontSize(16);
        doc.setTextColor(1, 150, 161);
        doc.text(tab.label, M, M + 7);
        doc.setDrawColor(220, 220, 220);
        doc.setLineWidth(0.3);
        doc.line(M, M + 11, A4_W - M, M + 11);

        // Contenido a continuación del título, paginado correctamente
        addSplitImage(imgData, imgH, CHAPTER_END_Y);
      }

      doc.save('documentacion-tecnica-mipymex-caabsa.pdf');

    } catch (err) {
      console.error('Error generando PDF:', err);
      alert('Error al generar el PDF. Intente de nuevo.');
    } finally {
      setIsGeneratingPDF(false);
      setActiveTab(savedTab);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-teal-50 rounded-lg">
            <Icon name="book" size={24} color="#0196A1" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Centro de Ayuda</h1>
            <p className="text-sm text-gray-500 mt-1">
              Documentación completa de la plataforma MiPymex CAABSA — Solo para administradores del sistema
            </p>
          </div>
        </div>
        <button
          onClick={generatePDF}
          disabled={isGeneratingPDF}
          className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isGeneratingPDF ? (
            <>
              <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Generando...
            </>
          ) : (
            <>
              <Icon name="file" size={16} />
              Exportar PDF
            </>
          )}
        </button>
      </div>

      {/* Contenido con tabs */}
      <div id="ayuda-content" className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 pt-4 overflow-x-auto">
          <Tabs items={TABS} activeTab={activeTab} onChange={setActiveTab} />
        </div>
        <div className="p-6">
          {activeTab === 'vision'       && <TabVisionGeneral />}
          {activeTab === 'modulos'      && <TabModulos />}
          {activeTab === 'flujo'        && <TabFlujoSolicitudes />}
          {activeTab === 'roles'        && <TabRolesPermisos />}
          {activeTab === 'api'          && <TabReferenciaAPI backendUrl={backendUrl} />}
          {activeTab === 'parametricos' && <TabParametricos />}
          {activeTab === 'despliegue'   && <TabDespliegueCloud />}
          {activeTab === 'seguridad'    && <TabSeguridad />}
          {activeTab === 'tecnica'      && <TabGuiaTecnica />}
        </div>
      </div>
    </div>
  );
};

export default AyudaManagement;
